import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  Copy,
  ExternalLink,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Search,
  UserCheck,
  UserX,
  Users,
  X,
} from "lucide-react";
import {
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { AccessGroupItem, UserAccessEntry, UserItem } from "@/features/identity-hub/api/identity-hub.api";
import { isPerTenantAssignable } from "@/features/identity-hub/lib/access-scope";
import { isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";
import { useProgressiveList } from "@/hooks/useProgressiveList";
import { IamListError } from "./IamListError";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { OrgMembersBulkBar } from "./OrgMembersBulkBar";
import { UserPhotoMark } from "./UserPhotoMark";

type MemberStatusFilter = "all" | "active" | "invited" | "suspended";
type MemberStatusKind = Exclude<MemberStatusFilter, "all">;

const STATUS_FILTERS: MemberStatusFilter[] = ["all", "active", "invited", "suspended"];

const PROGRESSIVE_BATCH_SIZE = 30;

const ROLE_SEPARATOR = " · ";

const TABLE_GRID = "grid-cols-[minmax(0,1fr)_minmax(0,200px)_88px_64px]";
const TABLE_GRID_WITH_SELECT = "grid-cols-[16px_minmax(0,1fr)_minmax(0,200px)_88px_64px]";

const CONTROL_BORDER_CLASS =
  "rounded-md border border-input-border bg-background outline-none focus:border-ring focus:ring-1 focus:ring-ring/40";

const STATUS_PILL_CLASS: Record<MemberStatusKind | "other", string> = {
  active: "bg-success-subtle text-success-subtle-foreground",
  invited: "border border-border bg-muted text-foreground",
  suspended: "bg-destructive/10 text-destructive",
  other: "bg-muted text-muted-foreground",
};

const ROW_ACTION_REVEAL_CLASS =
  "opacity-0 transition-opacity group-hover/member:opacity-100 group-focus-within/member:opacity-100";

function memberStatusKind(user: UserItem): MemberStatusKind | null {
  if (user.pendingInvite?.hasInvite) return "invited";
  if (user.status === "DEACTIVATED") return "suspended";
  if (user.status === "ACTIVE") return "active";
  return null;
}

function memberOrgAccessState(
  user: UserItem,
  orgId: string,
  accessByUser: Map<string, UserAccessEntry[]>,
) {
  const userAccess = accessByUser.get(user.id);
  const entries = (userAccess ?? []).filter((e) => e.access.tenantId === orgId);
  const isAdgeStaff = isSuperTenantMember(userAccess);
  const isFixedSlot =
    entries.length > 0 && entries.some((e) => !isPerTenantAssignable(e.access));
  const canRemove =
    entries.length > 0 &&
    entries.every((e) => isPerTenantAssignable(e.access)) &&
    user.accessIds.length > 1;
  const isSelectable = entries.length > 0 && !isFixedSlot && !isAdgeStaff;
  return { entries, isAdgeStaff, isFixedSlot, canRemove, isSelectable };
}

export type OrgDrawerMembersTabProps = {
  orgId: string;
  orgName: string;
  members: UserItem[];
  /** Server membership total. Search copy uses this so it matches the tab badge. */
  memberTotal?: number;
  membersLoading?: boolean;
  /** First-page failure. Shown instead of the loading spinner. */
  membersError?: unknown;
  onRetryMembers?: () => void;
  hasMoreMembers?: boolean;
  isFetchingMoreMembers?: boolean;
  /** Next page failed. The loaded rows stay; the footer offers a retry. */
  membersNextPageFailed?: boolean;
  onLoadMoreMembers?: () => void;
  accessByUser: Map<string, UserAccessEntry[]>;
  assignableRoles: AccessGroupItem[];
  /** Every role on this org, including managed ones — the role filter lists these. Defaults to `assignableRoles`. */
  roles?: AccessGroupItem[];
  initialRoleFilterId?: string | null;
  canManageMembers: boolean;
  hasRoles: boolean;
  onJumpToPerson: (userId: string) => void;
  onRemoveFromOrg: (userId: string) => void;
  onBulkChangeRole?: (userIds: string[], accessId: string) => void;
  onBulkAddRole?: (userIds: string[], accessId: string) => void;
  onBulkResendInvite?: (inviteIds: string[]) => void;
  onBulkRemoveFromOrg?: (userIds: string[]) => void;
  onSelectedCountChange?: (count: number) => void;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  isBulkBusy?: boolean;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (user: UserItem) => void;
  noRolesEmpty: ReactNode;
};

export function OrgDrawerMembersTab({
  orgId,
  orgName,
  members,
  memberTotal,
  membersLoading = false,
  membersError,
  onRetryMembers,
  hasMoreMembers = false,
  isFetchingMoreMembers = false,
  membersNextPageFailed = false,
  onLoadMoreMembers,
  accessByUser,
  assignableRoles,
  roles = assignableRoles,
  initialRoleFilterId,
  canManageMembers,
  hasRoles,
  onJumpToPerson,
  onRemoveFromOrg,
  onBulkChangeRole,
  onBulkAddRole,
  onBulkResendInvite,
  onBulkRemoveFromOrg,
  onSelectedCountChange,
  isRemovingMember,
  removingMemberId,
  isBulkBusy,
  onEditProfile,
  onSuspend,
  onUnsuspend,
  onOpenAuditLog,
  noRolesEmpty,
}: OrgDrawerMembersTabProps) {
  const { t } = useTranslation();
  const tb = (key: string, defaultValue: string, opts?: Record<string, unknown>) =>
    t(`iam.orgDrawer.membersTab.bulk.${key}`, { defaultValue, ...opts });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<MemberStatusFilter>("all");
  const [roleFilterId, setRoleFilterId] = useState<string>(initialRoleFilterId ?? "all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const onLoadMoreRef = useRef(onLoadMoreMembers);
  onLoadMoreRef.current = onLoadMoreMembers;
  const requestedAtLength = useRef(-1);

  useEffect(() => {
    requestedAtLength.current = -1;
    setSelectedIds(new Set());
    setSearch("");
    setStatusFilter("all");
    setRoleFilterId(initialRoleFilterId ?? "all");
  }, [orgId, initialRoleFilterId]);

  useEffect(() => {
    onSelectedCountChange?.(selectedIds.size);
  }, [selectedIds.size, onSelectedCountChange]);

  useEffect(() => () => onSelectedCountChange?.(0), [onSelectedCountChange]);

  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (membersById.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [membersById]);

  const statusCounts = useMemo(() => {
    const counts: Record<MemberStatusFilter, number> = {
      all: members.length,
      active: 0,
      invited: 0,
      suspended: 0,
    };
    for (const user of members) {
      const kind = memberStatusKind(user);
      if (kind) counts[kind] += 1;
    }
    return counts;
  }, [members]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((user) => {
      if (statusFilter !== "all" && memberStatusKind(user) !== statusFilter) return false;
      if (roleFilterId !== "all") {
        const entries = (accessByUser.get(user.id) ?? []).filter((e) => e.access.tenantId === orgId);
        if (!entries.some((e) => e.accessId === roleFilterId)) return false;
      }
      if (!q) return true;
      return (
        user.displayName.toLowerCase().includes(q) ||
        user.email.toLowerCase().includes(q)
      );
    });
  }, [members, search, statusFilter, roleFilterId, accessByUser, orgId]);

  const { visibleCount, hasMore, sentinelRef } = useProgressiveList(filteredMembers.length, {
    initialBatch: PROGRESSIVE_BATCH_SIZE,
    loadMore: PROGRESSIVE_BATCH_SIZE,
  });
  const visibleMembers = useMemo(
    () => filteredMembers.slice(0, visibleCount),
    [filteredMembers, visibleCount],
  );

  // The client window only reveals rows already fetched. When that window is
  // exhausted and the tenant query has another page, load it — same idea as
  // the role drawer sentinel, including while a filter hides the current page.
  // A failed next page leaves `members.length` unchanged, so the length guard
  // would never ask again. Clear it, and wait for the retry control.
  useEffect(() => {
    if (membersNextPageFailed) {
      requestedAtLength.current = -1;
      return;
    }
    if (!hasMoreMembers || isFetchingMoreMembers) return;
    if (membersLoading && members.length === 0) return;
    if (visibleCount < filteredMembers.length) return;
    if (requestedAtLength.current === members.length) return;
    requestedAtLength.current = members.length;
    onLoadMoreRef.current?.();
  }, [
    membersNextPageFailed,
    hasMoreMembers,
    isFetchingMoreMembers,
    membersLoading,
    members.length,
    visibleCount,
    filteredMembers.length,
  ]);

  const retryLoadMoreMembers = () => {
    requestedAtLength.current = members.length;
    onLoadMoreRef.current?.();
  };

  const selectableIds = useMemo(
    () =>
      members
        .filter((user) => memberOrgAccessState(user, orgId, accessByUser).isSelectable)
        .map((user) => user.id),
    [members, orgId, accessByUser],
  );

  const selectedUsers = useMemo(
    () => [...selectedIds].flatMap((id) => membersById.get(id) ?? []),
    [selectedIds, membersById],
  );

  const selectedRemovableIds = useMemo(
    () =>
      selectedUsers
        .filter((user) => memberOrgAccessState(user, orgId, accessByUser).canRemove)
        .map((user) => user.id),
    [selectedUsers, orgId, accessByUser],
  );

  const selectedInviteIds = useMemo(
    () => selectedUsers.flatMap((user) => (user.pendingInvite ? [user.pendingInvite.inviteId] : [])),
    [selectedUsers],
  );

  const roleHolderCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const user of selectedUsers) {
      for (const entry of accessByUser.get(user.id) ?? []) {
        if (entry.access.tenantId !== orgId) continue;
        counts.set(entry.accessId, (counts.get(entry.accessId) ?? 0) + 1);
      }
    }
    return counts;
  }, [selectedUsers, accessByUser, orgId]);

  const selectedCount = selectedIds.size;
  const isSelectionMode = canManageMembers && selectedCount > 0;
  // Select-all claims the whole organization. Until the last page is in, and
  // while a load error is showing, that claim is false.
  const canSelectEveryMember =
    membersError == null && !hasMoreMembers && !isFetchingMoreMembers && !membersNextPageFailed;
  const allSelectableSelected =
    canSelectEveryMember &&
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));
  const someSelectableSelected = selectableIds.some((id) => selectedIds.has(id));
  const tableGridClass = canManageMembers ? TABLE_GRID_WITH_SELECT : TABLE_GRID;

  const toggleMember = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(selectableIds));
  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelectAll = () => {
    if (!canSelectEveryMember) return;
    if (allSelectableSelected) clearSelection();
    else selectAll();
  };

  const runBulk = (action: () => void) => {
    action();
    clearSelection();
  };

  if (membersError != null) {
    return <IamListError error={membersError} onRetry={() => onRetryMembers?.()} />;
  }

  if (members.length === 0) {
    if (membersLoading || hasMoreMembers || isFetchingMoreMembers) {
      return (
        <div
          className="flex items-center justify-center py-12"
          data-testid="org-drawer-members-loading"
          aria-label={t("iam.orgDrawer.membersTab.loading", "Loading members")}
        >
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!hasRoles) {
      return noRolesEmpty;
    }
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center" data-testid="org-drawer-members-empty">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.orgDrawer.members.empty.title", "No members yet")}</p>
        <p className="text-xs text-muted-foreground">
          {t("iam.orgDrawer.members.empty.body", "Add people to this organization to get started.")}
        </p>
      </div>
    );
  }

  const statusLabels: Record<MemberStatusFilter, string> = {
    all: t("iam.orgDrawer.membersTab.statusAll", "All"),
    active: t("iam.orgDrawer.membersTab.statusActive", "Active"),
    invited: t("iam.orgDrawer.membersTab.statusInvited", "Invited"),
    suspended: t("iam.orgDrawer.membersTab.statusSuspended", "Suspended"),
  };

  return (
    <div className="flex flex-col gap-4 px-6 py-5" data-testid="org-drawer-members-tab">
      {isSelectionMode ? (
        <OrgMembersBulkBar
          orgName={orgName}
          selectedCount={selectedCount}
          selectableCount={selectableIds.length}
          assignableRoles={assignableRoles}
          roleHolderCounts={roleHolderCounts}
          removableCount={selectedRemovableIds.length}
          resendableCount={selectedInviteIds.length}
          isBusy={isBulkBusy}
          canSelectAllMembers={canSelectEveryMember}
          onToggleAll={toggleSelectAll}
          onSelectAll={selectAll}
          onClear={clearSelection}
          onChangeRole={
            onBulkChangeRole ? (accessId) => runBulk(() => onBulkChangeRole([...selectedIds], accessId)) : undefined
          }
          onAddRole={
            onBulkAddRole ? (accessId) => runBulk(() => onBulkAddRole([...selectedIds], accessId)) : undefined
          }
          onResendInvite={
            onBulkResendInvite ? () => runBulk(() => onBulkResendInvite(selectedInviteIds)) : undefined
          }
          onRemoveFromOrg={
            onBulkRemoveFromOrg ? () => runBulk(() => onBulkRemoveFromOrg(selectedRemovableIds)) : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("iam.orgDrawer.membersTab.searchPlaceholder", {
                count: memberTotal ?? members.length,
                defaultValue: "Search {{count}} members by name or email…",
              })}
              className={cn(
                CONTROL_BORDER_CLASS,
                "h-9 w-full min-w-0 pl-9 pr-3 text-[13px] placeholder:text-muted-foreground/70",
              )}
              data-testid="org-drawer-members-search"
            />
          </div>
          <div
            className="flex h-9 shrink-0 items-center gap-0.5 rounded-md border border-border bg-muted p-0.5"
            role="group"
            aria-label={t("iam.orgDrawer.membersTab.statusFilterAria", "Filter by status")}
          >
            {STATUS_FILTERS.map((id) => {
              const isActive = statusFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setStatusFilter(id)}
                  className={cn(
                    "flex h-full items-center gap-1 rounded-sm px-2.5 text-[11px] font-medium transition-colors",
                    isActive
                      ? "border border-border bg-background text-primary shadow-sm"
                      : "text-foreground hover:bg-background/60",
                  )}
                >
                  {statusLabels[id]}
                  <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                    {statusCounts[id]}
                  </span>
                </button>
              );
            })}
          </div>
          {roles.length > 0 && (
            <div className="relative shrink-0">
              <select
                value={roleFilterId}
                onChange={(e) => setRoleFilterId(e.target.value)}
                className={cn(CONTROL_BORDER_CLASS, "h-9 appearance-none pl-3 pr-8 text-xs text-foreground")}
                aria-label={t("iam.orgDrawer.membersTab.roleFilterAria", "Filter by role")}
                data-testid="org-drawer-members-role-filter"
              >
                <option value="all">{t("iam.orgDrawer.membersTab.allRoles", "All roles")}</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-3 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
            </div>
          )}
        </div>
      )}

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border">
        <div
          className={cn(
            "grid items-center gap-3 border-b border-border bg-muted px-4 py-2 text-[10px] font-medium uppercase tracking-[0.4px] text-muted-foreground",
            tableGridClass,
          )}
        >
          {canManageMembers && (
            <Checkbox
              checked={
                canSelectEveryMember
                  ? allSelectableSelected
                    ? true
                    : someSelectableSelected
                      ? "indeterminate"
                      : false
                  : someSelectableSelected
                    ? "indeterminate"
                    : false
              }
              onCheckedChange={toggleSelectAll}
              aria-label={
                canSelectEveryMember
                  ? tb("selectAllAria", "Select all members on this organization")
                  : tb(
                      "selectAllPendingAria",
                      "Select all is available after every member page has loaded",
                    )
              }
              disabled={isBulkBusy || selectableIds.length === 0 || !canSelectEveryMember}
            />
          )}
          <span>{t("iam.orgDrawer.membersTab.columnPerson", "Person")}</span>
          <span className="truncate">
            {t("iam.orgDrawer.membersTab.columnRoles", { orgName, defaultValue: "Roles in {{orgName}}" })}
          </span>
          <span>{t("iam.orgDrawer.membersTab.columnStatus", "Status")}</span>
          <span aria-hidden />
        </div>

        {filteredMembers.length === 0 && (
          <p className="py-8 text-center text-xs text-muted-foreground">
            {t("iam.orgDrawer.membersTab.noFilterMatch", "No members match your filters.")}
          </p>
        )}

        {visibleMembers.map((user) => {
          const { entries, isAdgeStaff, isFixedSlot, canRemove, isSelectable } = memberOrgAccessState(
            user,
            orgId,
            accessByUser,
          );
          const roleNames = entries.map((e) => e.access.name).join(ROLE_SEPARATOR);
          const isSelected = selectedIds.has(user.id);
          const isRemoving = isRemovingMember && removingMemberId === user.id;
          const isRemoveDisabled = !canRemove || isRemoving;
          const statusKind = memberStatusKind(user);
          const statusLabel =
            statusKind === "invited"
              ? t("iam.orgDrawer.membersTab.statusInvited", "Invited")
              : t(`iam.personDrawer.status.${user.status}`, user.status);

          return (
            <div
              key={user.id}
              className={cn(
                "group/member grid items-center gap-3 border-b border-border-subtle px-4 py-2.5 transition-colors last:border-b-0 hover:bg-muted",
                tableGridClass,
                isSelected && "bg-primary-subtle hover:bg-primary-subtle",
              )}
            >
              {canManageMembers && (
                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  {isSelectable ? (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleMember(user.id)}
                      aria-label={tb("selectMemberAria", "Select {{name}}", { name: user.displayName })}
                      disabled={isBulkBusy}
                    />
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex size-4 items-center justify-center text-muted-foreground">
                            {isAdgeStaff ? (
                              <Lock className="size-3.5" aria-hidden />
                            ) : (
                              <Checkbox checked={false} disabled aria-hidden className="opacity-40" />
                            )}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[240px]">
                          {isAdgeStaff
                            ? tb("adgeStaffExcludedTooltip", "Adge staff are excluded from bulk actions.")
                            : isFixedSlot
                              ? t("iam.orgDrawer.membersTab.fixedSlotTooltip")
                              : tb("notSelectableTooltip", "This member can't be included in bulk actions.")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}

              <div
                role="button"
                tabIndex={0}
                onClick={() => onJumpToPerson(user.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onJumpToPerson(user.id);
                  }
                }}
                className="flex min-w-0 cursor-pointer items-center gap-2.5 text-left"
              >
                <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                  <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="md" className="shrink-0" />
                </ImagePreviewTrigger>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[13px] font-medium leading-tight text-foreground">
                      {user.displayName}
                    </span>
                    {isAdgeStaff && (
                      <span className="shrink-0 rounded-full bg-warning-subtle px-1.5 py-[3px] text-[9px] font-semibold leading-none text-warning-subtle-foreground">
                        {t("iam.orgDrawer.platformBadge", "Adge")}
                      </span>
                    )}
                  </div>
                  <span className="truncate text-[11px] leading-tight text-muted-foreground">{user.email}</span>
                </div>
              </div>

              <span className="min-w-0 truncate text-xs text-foreground/80">{roleNames}</span>

              <span
                className={cn(
                  "w-fit rounded-full px-1.5 py-[3px] text-[10px] font-medium leading-none",
                  STATUS_PILL_CLASS[statusKind ?? "other"],
                )}
              >
                {statusLabel}
              </span>

              <div className="flex items-center justify-end gap-1">
                {canManageMembers && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={cn(ROW_ACTION_REVEAL_CLASS, isRemoving && "opacity-100")}>
                          <button
                            type="button"
                            onClick={() => !isRemoveDisabled && onRemoveFromOrg(user.id)}
                            disabled={isRemoveDisabled}
                            className="flex size-7 shrink-0 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label={t("iam.orgDrawer.membersTab.removeFromOrg", { name: user.displayName })}
                            aria-busy={isRemoving}
                          >
                            {isRemoving ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <X className="size-3.5" />
                            )}
                          </button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {user.accessIds.length <= 1
                          ? t("iam.roleDrawer.members.lastAccessTooltip", "Can't revoke — this is the user's only access")
                          : isFixedSlot
                            ? t("iam.orgDrawer.membersTab.fixedSlotTooltip")
                            : t("iam.personDrawer.orgs.removeFromOrg", { org: orgName })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        ROW_ACTION_REVEAL_CLASS,
                        "flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background hover:bg-muted data-[state=open]:opacity-100",
                      )}
                      aria-label={t("iam.personDrawer.actions.more", "More actions")}
                    >
                      <MoreHorizontal className="size-3.5 text-foreground/80" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem className="gap-2" onClick={() => onEditProfile?.(user)}>
                      <Pencil className="h-3.5 w-3.5" />
                      {t("iam.personDrawer.actions.editProfile")}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2" onClick={() => navigator.clipboard.writeText(user.email)}>
                      <Copy className="h-3.5 w-3.5" />
                      {t("iam.personDrawer.actions.copyEmail")}
                    </DropdownMenuItem>
                    {onOpenAuditLog && (
                      <DropdownMenuItem className="gap-2" onClick={() => onOpenAuditLog(user)}>
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t("iam.personDrawer.actions.viewAuditLog")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {user.status === "DEACTIVATED" ? (
                      <DropdownMenuItem className="gap-2" onClick={() => onUnsuspend?.(user)}>
                        <UserCheck className="h-3.5 w-3.5" />
                        {t("iam.personDrawer.actions.unsuspend")}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem variant="destructive" onClick={() => onSuspend?.(user)}>
                        <UserX className="h-3.5 w-3.5" />
                        {t("iam.personDrawer.actions.suspend")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}

        {hasMore && (
          <div
            ref={sentinelRef}
            className="flex h-10 items-center justify-center text-[11px] text-muted-foreground"
          >
            {t("iam.orgDrawer.membersTab.scrollToLoadMore", {
              count: filteredMembers.length - visibleCount,
              defaultValue_one: "Scroll to load {{count}} more…",
              defaultValue_other: "Scroll to load {{count}} more…",
            })}
          </div>
        )}
        {membersNextPageFailed && !isFetchingMoreMembers && (
          <div
            className="flex min-h-11 items-center justify-center gap-2 text-[11px] text-muted-foreground"
            data-testid="org-drawer-members-load-more-error"
            role="alert"
          >
            <span>{t("iam.orgDrawer.membersTab.loadMoreError", "Couldn't load more members.")}</span>
            <button
              type="button"
              onClick={retryLoadMoreMembers}
              className="inline-flex min-h-11 min-w-11 items-center justify-center px-2 font-medium text-foreground underline-offset-2 outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              {t("iam.dashboard.retry", "Retry")}
            </button>
          </div>
        )}
        {!hasMore && (isFetchingMoreMembers || (hasMoreMembers && !membersNextPageFailed)) && (
          <div
            className="flex h-10 items-center justify-center text-[11px] text-muted-foreground"
            data-testid="org-drawer-members-loading-more"
          >
            {isFetchingMoreMembers ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("iam.orgDrawer.membersTab.loadingMore", "Loading more…")
            )}
          </div>
        )}
      </div>
    </div>
  );
}
